import { supabase } from './client';
import { fetchReportBlob } from '@/services/offlineReportService';

/**
 * Mở file báo cáo mà KHÔNG phát hành URL có thể chia sẻ.
 *
 * Bản trước ký signed URL (TTL 1 giờ). Signed URL là bearer token: ai cầm link
 * đều mở được, không cần đăng nhập — copy link dán vào cửa sổ ẩn danh vẫn xem
 * được báo cáo của người khác. Sai với chính sách: chỉ chủ báo cáo, admin và
 * BOD được xem, và phải đang đăng nhập.
 *
 * Cách hiện tại: tải bytes bằng phiên đăng nhập của người dùng rồi trả về
 * `blob:` URL.
 *   - File mới (Storage): SDK `download()` đính JWT → RLS trên storage.objects
 *     (`can_read_report_file`) quyết định. Không có quyền → 4xx, không có URL.
 *   - File cũ (Drive): qua edge function `download-from-drive`, function tự
 *     kiểm tra người gọi đọc được dòng `reports` tương ứng mới trả bytes.
 *   - `blob:` URL chỉ sống trong browsing context đã tạo nó; dán sang tab ẩn
 *     danh / máy khác là vô nghĩa. Ngoài ra bị thu hồi sau BLOB_URL_TTL_MS.
 */

export const REPORT_BUCKET = 'report-files';

/** Đủ để mở/tải xong; sau đó URL chết dù tab kia còn mở (tab đã nạp xong file). */
const BLOB_URL_TTL_MS = 10 * 60 * 1000;

export interface ReportFileRefs {
  id?: string | null;
  file_id?: string | null;
  supabase_file_path?: string | null;
  full_url?: string | null;
  ai_storage_path?: string | null;
  ai_version_url?: string | null;
  report_name?: string | null;
}

function toBlobUrl(blob: Blob): string {
  const url = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
  return url;
}

/** Link Drive dạng /file/d/<id>/view hoặc ?id=<id> → id. */
function driveIdFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/d\/([A-Za-z0-9_-]{10,})/) ?? url.match(/[?&]id=([A-Za-z0-9_-]{10,})/);
  return m ? m[1] : null;
}

async function storageBlob(path: string, nameHint?: string | null): Promise<Blob> {
  const { data, error } = await supabase.storage.from(REPORT_BUCKET).download(path);
  if (error || !data) {
    throw new Error(`Không tải được file: ${error?.message ?? 'Storage không trả về dữ liệu'}`);
  }
  // Storage đôi khi trả Content-Type chung chung; PDF không có type sẽ bị tải
  // xuống thay vì mở trong tab.
  if (!data.type || data.type === 'application/octet-stream') {
    const lower = (nameHint ?? path).toLowerCase();
    if (lower.endsWith('.pdf')) return new Blob([data], { type: 'application/pdf' });
  }
  return data;
}

/** URL mở file gốc — luôn là `blob:` URL tạo từ phiên của người đang đăng nhập. */
export async function getReportFileUrl(report: ReportFileRefs): Promise<string> {
  if (report.supabase_file_path) {
    return toBlobUrl(await storageBlob(report.supabase_file_path, report.report_name));
  }

  const fileId = report.file_id ?? driveIdFromUrl(report.full_url);
  if (fileId) {
    const result = await fetchReportBlob({
      id: report.id ?? '',
      report_name: report.report_name,
      file_id: fileId,
    });
    if (!result.ok) {
      throw new Error('message' in result ? result.message : 'Không tải được file');
    }
    return toBlobUrl(result.blob);
  }

  throw new Error('Báo cáo này chưa có file đính kèm.');
}

/**
 * URL mở bản AI.
 * Bản AI cũ trên Drive không có dòng `reports.file_id` tương ứng nên
 * `download-from-drive` không xác minh được → chỉ còn cách mở link Drive, quyền
 * do Drive quản. Mọi bản AI từ 2026-08-24 đều nằm trong Storage nên đi đường
 * an toàn.
 */
export async function getReportAiFileUrl(report: ReportFileRefs): Promise<string> {
  if (report.ai_storage_path) {
    return toBlobUrl(await storageBlob(report.ai_storage_path, report.report_name));
  }
  if (report.ai_version_url) return report.ai_version_url;
  throw new Error('Báo cáo này chưa có bản AI.');
}

/** Có file để mở hay không — dùng để quyết định hiện nút. */
export function hasReportFile(report: ReportFileRefs): boolean {
  return Boolean(report.supabase_file_path || report.file_id || report.full_url);
}

/** Có bản AI để mở hay không. */
export function hasReportAiFile(report: ReportFileRefs): boolean {
  return Boolean(report.ai_storage_path || report.ai_version_url);
}
